/* eslint-disable @typescript-eslint/no-explicit-any */
import { MutableRefObject } from 'react';
import {
  Constants,
  DEFAULT_LOYALTY_OBJ,
  INTERNETBAR_PRICING_ZONE_OPTIONS,
  INTERNETBAR_PRICING_TIER_OPTIONS,
} from './constant';
import { IDuration } from '../components/InternetBarModal/InternetBarModal.types';
import DOMPurify from 'isomorphic-dompurify';
import {
  FeeDetailsUXLType,
  PricingInfoProps,
  PricingInfoPropsUXLType,
  EnrollmentSourceCodeDivisionMapping,
  EnrollmentSourceCodeMapping,
  EnrollmentSourceCodeTierKey,
  EnrollmentSourceCodeTierMapping,
  brandTierCategoriesType,
  InternetBarStateTypes,
} from '../InternetBar.types';
import { statesConstant } from '../../../modules/utils/constants/constants';
import { isMobile } from '../internetBar.helper';
import { getCookies, removeCookie, setCookie } from './cookieUtil';

type LoginDetails = {
  lastName: string;
  roomNumber: string;
};

export const setLoginModalInformation = (loginDetails: LoginDetails, MARSHA: string) => {
  const lastNameValue = loginDetails?.lastName;
  const roomNumberValue = loginDetails?.roomNumber;
  const cookieKey = 'PPv5UserInfo';
  const textEncoderInstance = new TextEncoder();
  const encodedText = textEncoderInstance.encode(`${MARSHA}|${lastNameValue}|${roomNumberValue}`);
  const cookieValue = window.btoa(String.fromCharCode.apply(null, Array.from(encodedText)));

  if (cookieValue) {
    setCookie(cookieKey, cookieValue);
  }
};

export const setDataInCookie = (cookieKey: string, cookieValue: string) => {
  const textEncoderInstance = new TextEncoder();
  const encodedText = textEncoderInstance.encode(cookieValue);
  const encodedCookieValue = window.btoa(
    Array.from(encodedText, encodedText => String.fromCharCode(encodedText)).join('')
  );

  if (cookieValue) {
    setCookie(cookieKey, encodedCookieValue);
  }
};

export const getDataInCookie = (cookieKey: string) => {
  const encodedCookieValue = getCookies(cookieKey);

  if (!encodedCookieValue) return '';

  const binary = window.atob(encodedCookieValue);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));

  return new TextDecoder().decode(bytes) ?? '';
};

export const submitDynamicForm = (body: any, postEndpoint: string): void => {
  const form = document.createElement('form');
  const parsedBody = body && body.trim() ? JSON.parse(body) : {};
  const submitFormEndpoint = postEndpoint.indexOf('://') === -1 ? `https://${postEndpoint}` : postEndpoint;
  form.setAttribute('method', 'POST');
  form.setAttribute('action', submitFormEndpoint);
  form.setAttribute('style', 'display: none');
  DOMPurify.sanitize(form, { RETURN_DOM_FRAGMENT: true });

  for (const property in parsedBody) {
    if (parsedBody[property] !== null) {
      const input = document.createElement('input');
      input.type = 'text';
      input.id = property;
      input.name = property;
      input.value = parsedBody[property];
      form.appendChild(input);
    }
  }

  // add the form inside the body
  document.getElementsByTagName('body')[0].appendChild(form); //pure javascript
  const failureCount = getDataInCookie('bonvoyLookupFailureCount');
  if (failureCount) {
    removeCookie('bonvoyLookupFailureCount');
    removeCookie('bonvoyLookupFailureCount');
  }
  form.submit();
};

export const loginBtnTriggerLSPPostParams = (
  loginDetails: LoginDetails,
  internetBarDataValues: Record<any, any>,
  currentStateMapping: InternetBarStateTypes
): string => {
  const lastNameValue = loginDetails?.lastName ?? '';
  const roomNumberValue = loginDetails?.roomNumber ?? '';

  const {
    MARSHA = '',
    LR = '',
    Z = '',
    ACTION = '',
    DOMAIN = '',
    MS,
    APS,
    zoneMapping = '',
  } = internetBarDataValues || {};
  const isUpgrade = getIsUpgrade(internetBarDataValues);
  const action = isUpgrade ? 'UPGRADE' : ACTION;

  if (lastNameValue && roomNumberValue) {
    try {
      setLoginModalInformation({ lastName: lastNameValue, roomNumber: roomNumberValue }, MARSHA);
      const obj = {
        MARSHA: MARSHA ? MARSHA : undefined,
        LR: !currentStateMapping?.showUpgradeModal ? LR : undefined,
        Z: !currentStateMapping?.showUpgradeModal ? Z : undefined,
        ACTION: action ? action : undefined,
        DOMAIN: DOMAIN ? DOMAIN : undefined,
        MS: MS || MS === '' ? MS : undefined,
        APS: APS,
        ROOM_NUMBER: roomNumberValue.trim(),
        LAST_NAME: lastNameValue.trim(),
        TIERS: zoneMapping[Z ?? ''] ?? undefined,
      };
      const body = JSON.stringify(obj);
      return body;
    } catch (e) {
      console.log('error', e);
      return '';
    }
  }

  return '';
};

export const loginBtnTrigger = (
  loginDetails: LoginDetails,
  internetBarDataValues: Record<any, any>,
  currentStateMapping: InternetBarStateTypes
): void => {
  const isUpgrade = getIsUpgrade(internetBarDataValues);
  const { postEndpoint = '', U_URLEndpoint = '' } = internetBarDataValues;
  const endpoint = isUpgrade ? U_URLEndpoint : postEndpoint;
  if (endpoint) {
    const body = loginBtnTriggerLSPPostParams(loginDetails, internetBarDataValues, currentStateMapping);
    submitDynamicForm(body, endpoint);
  }
};

export const connectBtnTriggerLSPPostParam = (
  isNoLoginInterfaceModal: boolean,
  internetBarDataValues: Record<string, any>
): string => {
  if (internetBarDataValues && internetBarDataValues?.['postEndpoint']) {
    if (isNoLoginInterfaceModal) {
      try {
        const zoneMapping = internetBarDataValues?.['zoneMapping'];
        const Z = internetBarDataValues?.['Z'] ?? '';
        let usage = internetBarDataValues?.['USAGE'] ?? '';
        const usageAttr = internetBarDataValues?.['usageAttr'] ?? '';
        const LR = internetBarDataValues?.['LR'] ?? '';
        const aemUSAGE = Constants.usageAEM[usageAttr] ? usageAttr : Constants.usage.FREE;

        const SP =
          usageAttr === Constants.usage.ELITE &&
          (zoneMapping[Z ?? 'GUEST'] === 'FP' || zoneMapping[Z ?? 'GUEST'] === 'PP')
            ? Constants.plan.PLAN2
            : Constants.plan.PLAN1;

        const sp = SP ?? '';

        if (
          sp === 'PLAN1' &&
          (zoneMapping[Z ?? 'PUBLIC'] === 'SF' ||
            zoneMapping[Z ?? 'GUEST'] === 'SF' ||
            zoneMapping[Z ?? 'MEETING'] === 'SF' ||
            zoneMapping[Z ?? 'CONFERENCE'] === 'SF')
        ) {
          usage = Constants.usage.FREE;
        }

        //newly added for LR TCTC value for FP and PP USAGE should be considered as FREE
        if (LR?.toUpperCase() === 'TCTC' && sp === 'PLAN1' && Z === 'GUEST') {
          usage = Constants.usage.FREE;
        }

        const obj = {
          MARSHA: internetBarDataValues?.['MARSHA'] ? internetBarDataValues?.['MARSHA'] : undefined,
          LR,
          Z,
          ACTION: 'CONNECTED',
          USAGE: usage ? usage : aemUSAGE,
          SP: sp,
          DURATION: internetBarDataValues?.['DURATION'] ? internetBarDataValues?.['DURATION'] : undefined,
          DOMAIN: internetBarDataValues?.['DOMAIN'] ? internetBarDataValues?.['DOMAIN'] : undefined,
          MS:
            internetBarDataValues?.['MS'] || internetBarDataValues?.['MS'] === ''
              ? internetBarDataValues?.['MS']
              : undefined,
          APS: internetBarDataValues?.['APS'],
          TIERS: zoneMapping[Z ?? ''] ?? undefined,
        };
        const body = JSON.stringify(obj);
        return body;
      } catch (error) {
        console.log('Error in connectBtnTrigger handler', error);
        return '';
      }
    }
  }
  return '';
};

// @todo :: Move noLoginInterfaceModal Logic here
// const handleConnectNoLogin = () => {}

export const connectBtnTrigger = (
  isNoLoginInterfaceModal: boolean,
  internetBarDataValues: Record<string, any>
): void => {
  const body = connectBtnTriggerLSPPostParam(isNoLoginInterfaceModal, internetBarDataValues);
  if (body && typeof body === 'string') {
    submitDynamicForm(body, internetBarDataValues?.['postEndpoint'] ?? '');
  }
};

export const getEnrolledUserFlag = (loyaltyEnrollmentSourceCode: string): boolean => {
  if (typeof window !== 'undefined' && window?.dataLayer && window?.sessionData) {
    const dataLayer = window?.dataLayer ?? {
      ...DEFAULT_LOYALTY_OBJ,
    };
    const sessionData = (window?.sessionData &&
      window?.sessionData?.cacheData &&
      window?.sessionData?.cacheData?.data &&
      window?.sessionData?.cacheData?.data?.AriesRewards) ?? {
      ...DEFAULT_LOYALTY_OBJ,
    };
    if (dataLayer && sessionData) {
      return (dataLayer?.['mr_enrolled'] && dataLayer?.['mr_enrollment_source'] === loyaltyEnrollmentSourceCode) ||
        (sessionData?.['mr_enrolled'] && sessionData?.['mr_enrollment_source'] === loyaltyEnrollmentSourceCode)
        ? true
        : false;
    }
    return false;
  }
  return false;
};

export const handleLoginBtnTrigger = (
  internetBarDataValues: Record<any, any>,
  isNoLoginInterfaceModal: boolean,
  isLoyaltyEnabled: boolean,
  isUserEnrolled: boolean,
  loginDetails: LoginDetails,
  currentStateMapping: InternetBarStateTypes
) => {
  const { LR = '', zoneMapping = '', ppv5Enrollment = false } = internetBarDataValues || {};

  const propertyTier = zoneMapping?.['GUEST'];
  if (!LR && propertyTier?.toUpperCase() === 'SF' && (isLoyaltyEnabled || (isUserEnrolled && ppv5Enrollment))) {
    connectBtnTrigger(
      propertyTier?.toUpperCase() === 'SF' && (isLoyaltyEnabled || (isUserEnrolled && ppv5Enrollment))
        ? true
        : isNoLoginInterfaceModal,
      internetBarDataValues
    );
  } else {
    loginBtnTrigger(loginDetails, internetBarDataValues, currentStateMapping);
  }
};

export const handleDurationDropdownKeyDownEvent = (
  event: KeyboardEvent,
  dropdownListRef: MutableRefObject<null>,
  durations: IDuration[] = [],
  item: IDuration
) => {
  if (event?.code?.toLowerCase() === 'arrowdown') {
    event?.stopPropagation();
    setTimeout(() => {
      const dropdownList = dropdownListRef?.current;
      if (dropdownList) {
        const list = (dropdownList as HTMLElement)?.children;
        const dIndex = durations?.findIndex(d => d.value === item.value);
        let dValue: Element | null = null;
        if (dIndex < list?.length - 1) {
          dValue = list[dIndex + 1];
        } else {
          dValue = list[0];
        }
        (dValue as HTMLElement)?.focus();
      }
    }, 20);
  } else if (event?.code?.toLowerCase() === 'arrowup') {
    event?.stopPropagation();
    setTimeout(() => {
      const dropdownList = dropdownListRef?.current;
      if (dropdownList) {
        const list = (dropdownList as HTMLElement)?.children;
        const dIndex = durations?.findIndex(d => d.value === item.value);
        let dValue: Element | null = null;
        if (dIndex - 1 >= 0) {
          dValue = list[dIndex - 1];
        } else {
          dValue = list[list?.length - 1] ?? list[0];
        }
        (dValue as HTMLElement)?.focus();
      }
    }, 20);
  } else {
    event?.stopPropagation();
  }
};

const getTierOneInfo = (
  settlementCurreny: PricingInfoPropsUXLType,
  hasOtherThanHotelQuotingCurrency: boolean,
  defaultCurrency: string
) => {
  const tierOnePricingInfo = {
    hasTierOne: true,
    tierOneId: 12345,
    tierOneName: INTERNETBAR_PRICING_TIER_OPTIONS?.tierOne,
    tierOneFee: '0',
    currencyOne: defaultCurrency,
  };

  tierOnePricingInfo.hasTierOne = true;
  tierOnePricingInfo.tierOneId = settlementCurreny?.externalRecordId;
  tierOnePricingInfo.tierOneName = settlementCurreny?.tierType ?? INTERNETBAR_PRICING_TIER_OPTIONS?.tierOne;

  tierOnePricingInfo.tierOneFee = hasOtherThanHotelQuotingCurrency
    ? (settlementCurreny?.feeDetails?.settlementFee ?? 0)?.toString()
    : (settlementCurreny?.feeDetails?.fee ?? 0)?.toString();

  tierOnePricingInfo.currencyOne = hasOtherThanHotelQuotingCurrency
    ? settlementCurreny?.feeDetails?.settlementCurrency?.code ?? ''
    : defaultCurrency;

  return {
    ...tierOnePricingInfo,
  };
};

const getZonePricingInfo = (
  settlementCurrenyDetails: Array<PricingInfoPropsUXLType>,
  defaultCurrency: string,
  hotelTier: string
) => {
  if (!hotelTier) {
    console.error('HOTEL TIER UNIDENTIFIED | Error in Catalog Data');
    return {
      zonePricingInfo: null,
      hasError: true,
    };
  }

  // Remove or update this logic post confirmation from the onsite team

  let pricingInfo: PricingInfoProps = {
    tierTwoFee: '',
    tierOneFee: '',
    hasTierOne: false,
    hasTierTwo: false,
    tierTwoId: 0,
    tierOneId: 0,
  };

  if (settlementCurrenyDetails?.length > 1) {
    const hasOtherThanHotelQuotingCurrencyForZone =
      settlementCurrenyDetails?.every(
        (settlementCurreny: { hasOtherThanHotelQuotingCurrency: boolean }) =>
          settlementCurreny?.hasOtherThanHotelQuotingCurrency
      ) ?? false;

    const showDefaultPricing =
      settlementCurrenyDetails?.every(
        (settlementCurreny: { hasOtherThanHotelQuotingCurrency: boolean }) =>
          !settlementCurreny?.hasOtherThanHotelQuotingCurrency
      ) ?? false;

    if (hasOtherThanHotelQuotingCurrencyForZone || hotelTier === 'FP') {
      let hasCurrencyError = false;
      settlementCurrenyDetails?.forEach((settlementCurreny: PricingInfoPropsUXLType) => {
        if (settlementCurreny?.tierType === INTERNETBAR_PRICING_TIER_OPTIONS.tierOne) {
          const tierOneInfo = getTierOneInfo(settlementCurreny, true, defaultCurrency);
          pricingInfo = {
            ...pricingInfo,
            ...tierOneInfo,
          };
        } else if (settlementCurreny?.tierType === INTERNETBAR_PRICING_TIER_OPTIONS.tierTwo) {
          pricingInfo.hasTierTwo = true;
          pricingInfo.tierTwoId = settlementCurreny?.externalRecordId;
          pricingInfo.tierTwoName = settlementCurreny?.tierType ?? INTERNETBAR_PRICING_TIER_OPTIONS.tierTwo;

          if (hotelTier === 'FP') {
            pricingInfo.tierTwoFee = settlementCurreny?.hasOtherThanHotelQuotingCurrency
              ? (settlementCurreny?.feeDetails?.settlementFee ?? 0)?.toString()
              : (settlementCurreny?.feeDetails?.fee ?? 0)?.toString();

            pricingInfo.currencyTwo = settlementCurreny?.hasOtherThanHotelQuotingCurrency
              ? settlementCurreny?.feeDetails?.settlementCurrency?.code ?? ''
              : defaultCurrency;
          } else {
            pricingInfo.tierTwoFee = (settlementCurreny?.feeDetails?.settlementFee ?? 0)?.toString();
            pricingInfo.currencyTwo = settlementCurreny?.feeDetails?.settlementCurrency?.code ?? '';
          }
          if (
            settlementCurreny?.hasOtherThanHotelQuotingCurrency &&
            !settlementCurreny?.feeDetails?.settlementCurrency &&
            !settlementCurreny?.feeDetails?.settlementCurrency?.code
          ) {
            hasCurrencyError = true;
          }
        }
      });
      if (hasCurrencyError) {
        return {
          zonePricingInfo: null,
          hasError: true,
        };
      }
    } else if (showDefaultPricing) {
      settlementCurrenyDetails?.forEach((settlementCurreny: PricingInfoPropsUXLType) => {
        if (settlementCurreny?.tierType === INTERNETBAR_PRICING_TIER_OPTIONS.tierOne) {
          const tierOneInfo = getTierOneInfo(settlementCurreny, false, defaultCurrency);
          pricingInfo = {
            ...pricingInfo,
            ...tierOneInfo,
          };
        } else if (settlementCurreny?.tierType === INTERNETBAR_PRICING_TIER_OPTIONS.tierTwo) {
          pricingInfo.hasTierTwo = true;
          pricingInfo.tierTwoId = settlementCurreny?.externalRecordId;
          pricingInfo.tierTwoName = settlementCurreny?.tierType ?? INTERNETBAR_PRICING_TIER_OPTIONS.tierTwo;
          pricingInfo.tierTwoFee = (settlementCurreny?.feeDetails?.fee ?? 0)?.toString();
          pricingInfo.currencyTwo = defaultCurrency;
        }
      });
    } else {
      console.error('PRICING INFO | PAID-PAID | Settlement Currency NOT ENABLED for all the Tiers');
      return {
        zonePricingInfo: null,
        hasError: true,
      };
    }
  } else {
    const tierOneInfo = getTierOneInfo(
      settlementCurrenyDetails?.[0],
      settlementCurrenyDetails?.[0]?.hasOtherThanHotelQuotingCurrency,
      defaultCurrency
    );
    pricingInfo = {
      ...pricingInfo,
      ...tierOneInfo,
    };
  }

  if (
    (hotelTier === 'PP' && pricingInfo.currencyOne !== pricingInfo.currencyTwo) ||
    (hotelTier === 'PP' && parseFloat(pricingInfo?.tierTwoFee) < parseFloat(pricingInfo?.tierOneFee))
  ) {
    console.error('PRICING INFO | PAID-PAID | Settlement Currency Code Mismatch');
    return {
      zonePricingInfo: null,
      hasError: true,
    };
  }

  return {
    zonePricingInfo: { ...pricingInfo },
    hasError: false,
  };
};

const getPricingTierInfo = (settlementCurrenyDetails: Array<PricingInfoPropsUXLType>) => {
  let pricingTierInfo = '';
  if (settlementCurrenyDetails && settlementCurrenyDetails?.length) {
    if (settlementCurrenyDetails?.length === 1) {
      if (settlementCurrenyDetails?.[0]?.tierType === 'Tier1') {
        if (settlementCurrenyDetails?.[0]?.feeDetails?.feeType === 'C') {
          pricingTierInfo = 'SF';
        } else if (settlementCurrenyDetails?.[0]?.feeDetails?.feeType === 'F') {
          pricingTierInfo = 'SP';
        }
      } else {
        console.error('PRICING INFO | 1 TIER | Missing First Tier');
      }
    } else {
      settlementCurrenyDetails?.forEach(settlementCurreny => {
        if (settlementCurreny?.tierType === 'Tier1' && settlementCurreny?.feeDetails?.feeType === 'C') {
          pricingTierInfo = 'FP';
        } else if (settlementCurreny?.tierType === 'Tier1' && settlementCurreny?.feeDetails?.feeType === 'F') {
          pricingTierInfo = 'PP';
        }
      });
    }
  }

  return pricingTierInfo;
};

export const settlementCurrencyHandler = (propertyPortalData: any, Z: string, defaultAEMCurrency: string) => {
  const settlementCurrenyDetails: Array<PricingInfoPropsUXLType> =
    propertyPortalData?.internet?.connectionPricingDetails ?? [];

  const defaultCurrency = propertyPortalData?.basicInformation?.currency ?? defaultAEMCurrency;

  let pricingInfo: PricingInfoProps = {
    tierTwoFee: '',
    tierOneFee: '',
    tierTwoId: 0,
    tierOneId: 0,
  };

  let pricingInfoHasError: boolean = false;

  try {
    if (!settlementCurrenyDetails?.length) {
      pricingInfoHasError = true;
    } else if (Z?.toUpperCase() === 'MEETING' || Z?.toUpperCase() === 'CONFERENCE') {
      const settlementCurrenyConferenceDetails = settlementCurrenyDetails?.filter(
        (settlementCurreny: { feeDetails: FeeDetailsUXLType; type: string }) =>
          settlementCurreny?.type === INTERNETBAR_PRICING_ZONE_OPTIONS?.conference && settlementCurreny?.feeDetails
      );

      const hotelTier = getPricingTierInfo(settlementCurrenyConferenceDetails) ?? 'SF';

      if (settlementCurrenyConferenceDetails && settlementCurrenyConferenceDetails?.length) {
        const { zonePricingInfo, hasError = false } =
          getZonePricingInfo(settlementCurrenyConferenceDetails, defaultCurrency, hotelTier) ?? {};

        if (!hasError) {
          pricingInfo = { ...(zonePricingInfo as PricingInfoProps) };
        } else {
          pricingInfoHasError = true;
        }
      } else {
        const hotelTier = 'SF';
        const { zonePricingInfo, hasError = false } =
          getZonePricingInfo(settlementCurrenyConferenceDetails, defaultCurrency, hotelTier) ?? {};
        pricingInfo = { ...(zonePricingInfo as PricingInfoProps) };
        pricingInfoHasError = hasError;
      }
    } else if (Z?.toUpperCase() === 'PUBLIC' || Z?.toUpperCase() === 'LOBBY') {
      const settlementCurrenyPublicDetails = settlementCurrenyDetails?.filter(
        (settlementCurreny: { feeDetails: FeeDetailsUXLType; type: string }) =>
          settlementCurreny?.type === INTERNETBAR_PRICING_ZONE_OPTIONS?.public && settlementCurreny?.feeDetails
      );

      const hotelTier = getPricingTierInfo(settlementCurrenyPublicDetails);

      if (settlementCurrenyPublicDetails && settlementCurrenyPublicDetails?.length) {
        const { zonePricingInfo, hasError = false } =
          getZonePricingInfo(settlementCurrenyPublicDetails, defaultCurrency, hotelTier) ?? {};

        if (!hasError) {
          pricingInfo = { ...(zonePricingInfo as PricingInfoProps) };
        } else {
          pricingInfoHasError = true;
        }
      } else {
        pricingInfoHasError = true;
      }
    } else {
      const settlementCurrenyGuestDetails: Array<PricingInfoPropsUXLType> = settlementCurrenyDetails?.filter(
        (settlementCurreny: { feeDetails: FeeDetailsUXLType; type: string }) =>
          settlementCurreny?.type === INTERNETBAR_PRICING_ZONE_OPTIONS?.guest && settlementCurreny?.feeDetails
      );

      const hotelTier = getPricingTierInfo(settlementCurrenyGuestDetails);

      if (settlementCurrenyGuestDetails && settlementCurrenyGuestDetails?.length) {
        const { zonePricingInfo, hasError = false } =
          getZonePricingInfo(settlementCurrenyGuestDetails, defaultCurrency, hotelTier) ?? {};

        if (!hasError) {
          pricingInfo = { ...(zonePricingInfo as PricingInfoProps) };
        } else {
          pricingInfoHasError = true;
        }
      } else {
        pricingInfoHasError = true;
      }
    }
    if (pricingInfoHasError) {
      return {
        pricingInfo: null,
        hasError: true,
      };
    } else {
      return {
        pricingInfo: { ...pricingInfo },
      };
    }
  } catch (error) {
    return {
      pricingInfo: null,
      hasError: true,
    };
  }
};

const getPricingTierEnrollmentSourceCode = (
  guestZoneMapping: EnrollmentSourceCodeTierKey,
  pricingTier: Array<EnrollmentSourceCodeTierMapping>
) => {
  if (pricingTier) {
    let enrollmentSourceCode: EnrollmentSourceCodeTierMapping = pricingTier?.find(
      (element: EnrollmentSourceCodeTierMapping) => element.key === guestZoneMapping
    ) as EnrollmentSourceCodeTierMapping;
    enrollmentSourceCode = enrollmentSourceCode?.value
      ? enrollmentSourceCode
      : (pricingTier?.find(
          (element: EnrollmentSourceCodeTierMapping) => element.key === ('*' as unknown as EnrollmentSourceCodeTierKey)
        ) as EnrollmentSourceCodeTierMapping);

    return enrollmentSourceCode?.value ?? '';
  }
  return '';
};

const getBrandTier = (brandTierCategories: Array<brandTierCategoriesType> | undefined, brandTier: string) => {
  const matchedBrandTier =
    brandTierCategories && brandTierCategories?.length
      ? brandTierCategories?.filter(
          (element: brandTierCategoriesType) =>
            element?.brandTier?.toLocaleLowerCase() === brandTier?.toLocaleLowerCase()
        )
      : null;
  return matchedBrandTier;
};

const loyaltyEnrollmentSourceCode = (
  sourceCodeMappingAEM: Array<EnrollmentSourceCodeDivisionMapping>,
  uxlGlobalDivision: string,
  uxlCountryCode: string,
  guestZoneMapping: EnrollmentSourceCodeTierKey,
  brandTier: string
) => {
  let finalEnrollmentSourceCode;
  let loyaltyAgeExperience;

  // Step I :: Filter the enrollment data-list for the Global Division (coming from the UXL) instances
  const matchedGlobalDivision = sourceCodeMappingAEM?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.globalDivision?.includes(uxlGlobalDivision)
  );

  // Step II :: Filter the country specific enrollments from the filtered Global Division List (matchedGlobalDivision)
  const matchedCountryCode = matchedGlobalDivision?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.countryCode?.includes(uxlCountryCode)
  );

  // Step III :: Check for the pricing tier specific enrollment source code
  // if the country specific enrollment data is present
  // else fallback to the Global division enrollment source code

  if (matchedCountryCode && matchedCountryCode?.length) {
    const matchedBrandTier = getBrandTier(matchedCountryCode?.[0]?.brandTierCategories, brandTier);
    finalEnrollmentSourceCode = getPricingTierEnrollmentSourceCode(
      guestZoneMapping,
      matchedBrandTier?.[0]?.pricingTier ?? matchedCountryCode?.[0]?.pricingTier
    );
    loyaltyAgeExperience = matchedBrandTier?.[0]?.experience;
  } else {
    const globalDivision: EnrollmentSourceCodeDivisionMapping = matchedGlobalDivision?.find(
      (element: EnrollmentSourceCodeDivisionMapping) => !element?.countryCode?.length
    ) as EnrollmentSourceCodeDivisionMapping;
    const matchedBrandTier = getBrandTier(globalDivision?.brandTierCategories, brandTier);
    finalEnrollmentSourceCode = getPricingTierEnrollmentSourceCode(
      guestZoneMapping,
      matchedBrandTier?.[0]?.pricingTier ?? globalDivision?.pricingTier
    );
    loyaltyAgeExperience = matchedBrandTier?.[0]?.experience;
  }

  return {
    finalEnrollmentSourceCode,
    loyaltyAgeExperience,
    weChatEnrollmentSourceCode: finalEnrollmentSourceCode,
  };
};

const getWeChatLoyaltyEnrollmentSourceCode = (
  sourceCodeMappingAEM: Array<EnrollmentSourceCodeDivisionMapping>,
  uxlGlobalDivision: string,
  uxlGlobalRegion: string,
  guestZoneMapping: EnrollmentSourceCodeTierKey
) => {
  let finalEnrollmentSourceCode;

  // Step I :: Filter the enrollment data-list for the Global Division (coming from the UXL) instances
  const matchedGlobalDivision = sourceCodeMappingAEM?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.globalDivision?.includes(uxlGlobalDivision)
  );

  // Step II :: Filter the region specific enrollments from the filtered Global Division List (matchedGlobalDivision)
  const matchedGlobalRegion = matchedGlobalDivision?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.region?.toLowerCase()?.includes(uxlGlobalRegion?.toLowerCase())
  );

  // Step III :: Check for the pricing tier specific enrollment source code
  // if the region specific enrollment data is present
  // else fallback to  @todo :: GET CONFIRMATION ON THE FALLBACK ENROLLMENT SOURCE CODE

  if (matchedGlobalRegion && matchedGlobalRegion?.length) {
    finalEnrollmentSourceCode = getPricingTierEnrollmentSourceCode(
      guestZoneMapping,
      matchedGlobalRegion?.[0]?.pricingTier
    );
  }

  return finalEnrollmentSourceCode;
};

export const isMBVMember = (userState: string) =>
  userState?.toLowerCase() === 'basic' || userState?.toLowerCase() === 'elite';

export const isWeChatEnabled = (actionType: string, uxlGlobalDivision: string, userState: string) =>
  isMobile() &&
  (uxlGlobalDivision === 'Greater China' || uxlGlobalDivision === 'Asia Pacific') &&
  !isMBVMember(userState) &&
  (actionType === statesConstant.connected || actionType === statesConstant?.in_stay);

export const getRegionEnrollmentSourceCode = (
  enrollmentSourceCodeMappingAEM: EnrollmentSourceCodeMapping,
  currentRegionUXL: any,
  loyaltyEnableFlag: boolean,
  guestZoneMapping: EnrollmentSourceCodeTierKey,
  loyaltyEnrollmentSourceCodeValue: string,
  enrollmentSourceCode: string,
  actionType: string,
  userState: string,
  weChatEnrollmentSourceCodes: Array<EnrollmentSourceCodeDivisionMapping>,
  brandTier: string
) => {
  const sourceCodeMappingAEM: EnrollmentSourceCodeMapping = enrollmentSourceCodeMappingAEM
    ? enrollmentSourceCodeMappingAEM
    : { loyalty: [], nonloyalty: [] };
  const uxlGlobalDivision = currentRegionUXL?.basicInformation?.globalDivision;
  const uxlGlobalRegion = currentRegionUXL?.basicInformation?.region;
  const uxlCountryCode = currentRegionUXL?.contactInformation?.address?.country?.code;
  const weChatEnabled = isWeChatEnabled(actionType, uxlGlobalDivision, userState);
  const weChatEnrollmentSourceCode = weChatEnabled
    ? getWeChatLoyaltyEnrollmentSourceCode(
        weChatEnrollmentSourceCodes,
        uxlGlobalDivision,
        uxlGlobalRegion,
        guestZoneMapping
      )
    : '';
  const { finalEnrollmentSourceCode, loyaltyAgeExperience } = loyaltyEnrollmentSourceCode(
    loyaltyEnableFlag ? sourceCodeMappingAEM?.loyalty : sourceCodeMappingAEM?.nonloyalty,
    uxlGlobalDivision,
    uxlCountryCode,
    guestZoneMapping,
    brandTier
  );

  const result = finalEnrollmentSourceCode
    ? finalEnrollmentSourceCode
    : loyaltyEnableFlag
    ? loyaltyEnrollmentSourceCodeValue
    : enrollmentSourceCode;

  return {
    finalEnrollmentSourceCode: result,
    weChatEnrollmentSourceCode: weChatEnrollmentSourceCode,
    loyaltyAgeExperience,
  };
};

export const replaceEnrollmentSourceCode = (
  joinNowLink: string,
  updatedEnrollmentSourceCode: string,
  returnToURL: string
) => {
  let updatedJoinNowLinkWithEnrollment = '';
  if (joinNowLink && joinNowLink?.includes('&enrollmentSourceCode=')) {
    const urlQueryParams = joinNowLink?.split('&');
    const updatedURLQueryParams = urlQueryParams?.map(urlQueryParam => {
      if (urlQueryParam?.includes('enrollmentSourceCode')) {
        const enrollment = urlQueryParam?.split('=');
        return `${enrollment[0]}=${updatedEnrollmentSourceCode}`;
      } else {
        return urlQueryParam;
      }
    });
    updatedJoinNowLinkWithEnrollment = updatedURLQueryParams.join('&');
  }

  if (updatedJoinNowLinkWithEnrollment && updatedJoinNowLinkWithEnrollment?.includes('&returnToUrl=')) {
    const urlQueryParams = updatedJoinNowLinkWithEnrollment?.split('&');
    const updatedURLQueryParams = urlQueryParams?.map(urlQueryParam => {
      if (urlQueryParam?.includes('returnToUrl')) {
        const returnTo = urlQueryParam?.split('=');
        return `${returnTo[0]}=${encodeURIComponent(returnToURL)}`;
      } else {
        return urlQueryParam;
      }
    });
    updatedJoinNowLinkWithEnrollment = updatedURLQueryParams.join('&');
  }
  return updatedJoinNowLinkWithEnrollment;
};

export const getZonePropertyTier = (pricingInfo: PricingInfoProps | null): EnrollmentSourceCodeTierKey => {
  let zonePropertyTier = 'SF';
  if (pricingInfo) {
    const pricingInfoTierOneFee = parseFloat(pricingInfo?.tierOneFee);
    const pricingInfoTierTwoFee = parseFloat(pricingInfo?.tierTwoFee);

    if (pricingInfoTierOneFee && pricingInfoTierTwoFee) {
      zonePropertyTier = 'PP';
    } else if (pricingInfoTierOneFee && !pricingInfoTierTwoFee) {
      zonePropertyTier = 'SP';
    } else if (!pricingInfoTierOneFee && pricingInfoTierTwoFee) {
      zonePropertyTier = 'FP';
    } else {
      zonePropertyTier = 'SF';
    }
  }
  return zonePropertyTier as unknown as EnrollmentSourceCodeTierKey;
};

export const getLoyaltyEnabledForConferenceZone = (
  sourceCodeMappingAEM: Array<EnrollmentSourceCodeDivisionMapping>,
  globalDivision: string,
  uxlCountryCode: string
) => {
  let enableLoyaltyForConferenceZone = false;
  // Step I :: Filter the enrollment data-list for the Global Division (coming from the UXL) instances
  const matchedGlobalDivision = sourceCodeMappingAEM?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.globalDivision?.includes(globalDivision)
  );

  // Step II :: Filter the country specific enrollments from the filtered Global Division List (matchedGlobalDivision)
  const matchedCountryCode = matchedGlobalDivision?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.countryCode?.includes(uxlCountryCode)
  );

  if (matchedCountryCode && matchedCountryCode?.length) {
    enableLoyaltyForConferenceZone = matchedCountryCode?.[0]?.enableLoyaltyForConferenceZone;
  } else {
    enableLoyaltyForConferenceZone = matchedGlobalDivision?.[0]?.enableLoyaltyForConferenceZone;
  }
  return enableLoyaltyForConferenceZone;
};

export const getEnableSkipEnrollmentVerbiage = (
  sourceCodeMappingAEM: Array<EnrollmentSourceCodeDivisionMapping>,
  globalDivision: string,
  uxlCountryCode: string
) => {
  let enableSkipEnrollmentVerbiage = false;
  // Step I :: Filter the enrollment data-list for the Global Division (coming from the UXL) instances
  const matchedGlobalDivision = sourceCodeMappingAEM?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.globalDivision?.includes(globalDivision)
  );

  // Step II :: Filter the country specific enrollments from the filtered Global Division List (matchedGlobalDivision)
  const matchedCountryCode = matchedGlobalDivision?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.countryCode?.includes(uxlCountryCode)
  );

  if (matchedCountryCode && matchedCountryCode?.length) {
    enableSkipEnrollmentVerbiage = matchedCountryCode?.[0]?.enableSkipEnrollmentVerbiage ?? false;
  } else {
    enableSkipEnrollmentVerbiage = matchedGlobalDivision?.[0]?.enableSkipEnrollmentVerbiage ?? false;
  }
  return enableSkipEnrollmentVerbiage;
};

export const getSFRoomLastNameValidationFlag = (
  sourceCodeMappingAEM: Array<EnrollmentSourceCodeDivisionMapping>,
  globalDivision: string,
  uxlCountryCode: string
) => {
  let isSFRoomLastNameValidationEnabled = true;
  // Step I :: Filter the enrollment data-list for the Global Division (coming from the UXL) instances
  const matchedGlobalDivision = sourceCodeMappingAEM?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.globalDivision?.includes(globalDivision)
  );

  // Step II :: Filter the country specific enrollments from the filtered Global Division List (matchedGlobalDivision)
  const matchedCountryCode = matchedGlobalDivision?.filter((element: EnrollmentSourceCodeDivisionMapping) =>
    element?.countryCode?.includes(uxlCountryCode)
  );

  if (matchedCountryCode && matchedCountryCode?.length) {
    isSFRoomLastNameValidationEnabled = matchedCountryCode?.[0]?.isSFRoomLastNameValidationEnabled ?? true;
  } else {
    isSFRoomLastNameValidationEnabled = matchedGlobalDivision?.[0]?.isSFRoomLastNameValidationEnabled ?? true;
  }
  return isSFRoomLastNameValidationEnabled;
};

export const getIsUpgrade = (internetBarDataValues: Record<string, any>): boolean => {
  const action = internetBarDataValues?.['ACTION']?.toString()?.toUpperCase();
  const zone = internetBarDataValues?.['Z']?.toString()?.toUpperCase();
  const upgradeEndpoint = internetBarDataValues?.['U_URLEndpoint'] ?? '';
  const propertyTier = internetBarDataValues?.['zoneMapping']?.['GUEST']?.toString()?.toUpperCase();

  return (
    action === 'CONNECTED' &&
    zone === 'GUEST' &&
    upgradeEndpoint !== '' &&
    (propertyTier === 'FP' || propertyTier === 'PP')
  );
};